// modules/cms/src/manifest.ts — CMS's nav contribution to the admin portal.
// Mirrors the routes makeCmsRoutes builds, one entry per content type, each
// carrying the capability that reveals it. The API re-checks the same
// cms:<cap> on every route (ADR-001 §14.2) — the menu only HIDES affordances.
//
// Kept in step with the route factory by review: every destination the console
// can reach should appear here, or it becomes unreachable once the tab strip
// is gone.
import { Calendar, Cookie, FileText, Image, Images, Library, Mail, Megaphone, Newspaper, Settings, Tag } from "lucide-solid";
import type { ModuleManifest } from "@erp/auth-kit/nav";

export const cmsAdminNav: ModuleManifest = {
  id: "cms",
  module: "cms",
  label: "Content",
  to: "/cms",
  icon: Newspaper,
  // ONE section of collapsible GROUPS (restructured 2026-09-02, the lib
  // precedent): groups became parent items with `children`, turning on the
  // sidebar's second level (collapsible parents + the hierarchy rail).
  // CMS routes are flat (/cms/<type>), so every parent's `to` is the module
  // landing "/cms" — matching-only, since a parent with children renders as a
  // toggle; the real child always wins the longest-prefix match. Newsletter
  // stays a loose leaf: a one-child group is a worse click than a flat row.
  // Parents carry no perm — buildNav shows them exactly when a child survives.
  sections: [
    {
      id: "cms-nav",
      label: "Content management",
      items: [
        {
          id: "cms-publishing",
          label: "Publishing",
          to: "/cms",
          icon: Megaphone,
          children: [
            { id: "cms-announcements", label: "Announcements", to: "/cms/announcements", icon: Megaphone, perm: "cms:announcements" },
            { id: "cms-tenders", label: "Tenders", to: "/cms/tenders", icon: FileText, perm: "cms:tenders" },
            { id: "cms-events", label: "Events", to: "/cms/events", icon: Calendar, perm: "cms:events" },
            { id: "cms-publications", label: "Publications", to: "/cms/publications", icon: FileText, perm: "cms:publications" },
            { id: "cms-publication-titles", label: "Publication titles", to: "/cms/publication-titles", icon: Library, perm: "cms:publications" },
            // HIDDEN leaves (audit UX-18): the create and edit forms have no
            // menu row; these give the breadcrumb their page's own name while
            // the sidebar keeps the list row lit (the nearest visible ancestor).
            { id: "cms-announcements-new", label: "New announcement", to: "/cms/announcements/new", perm: "cms:announcements", hidden: true },
            { id: "cms-announcements-edit", label: "Edit announcement", to: "/cms/announcements/$announcementId/edit", perm: "cms:announcements", hidden: true },
            { id: "cms-tenders-new", label: "New tender", to: "/cms/tenders/new", perm: "cms:tenders", hidden: true },
            { id: "cms-tenders-edit", label: "Edit tender", to: "/cms/tenders/$tenderId/edit", perm: "cms:tenders", hidden: true },
            { id: "cms-events-new", label: "New event", to: "/cms/events/new", perm: "cms:events", hidden: true },
            { id: "cms-events-edit", label: "Edit event", to: "/cms/events/$eventId/edit", perm: "cms:events", hidden: true },
            { id: "cms-publications-new", label: "New publication", to: "/cms/publications/new", perm: "cms:publications", hidden: true },
            { id: "cms-publications-edit", label: "Edit publication", to: "/cms/publications/$publicationId/edit", perm: "cms:publications", hidden: true },
            { id: "cms-publication-titles-new", label: "New publication title", to: "/cms/publication-titles/new", perm: "cms:publications", hidden: true },
            { id: "cms-publication-titles-edit", label: "Edit publication title", to: "/cms/publication-titles/$titleId/edit", perm: "cms:publications", hidden: true },
          ],
        },
        {
          id: "cms-media",
          label: "Media",
          to: "/cms",
          icon: Images,
          children: [
            { id: "cms-gallery", label: "Gallery", to: "/cms/gallery", icon: Images, perm: "cms:gallery" },
            { id: "cms-media-library", label: "Media library", to: "/cms/media", icon: Image, perm: "cms:media" },
            { id: "cms-gallery-new", label: "New photo", to: "/cms/gallery/new", perm: "cms:gallery", hidden: true },
            { id: "cms-gallery-edit", label: "Edit photo", to: "/cms/gallery/$galleryId/edit", perm: "cms:gallery", hidden: true },
          ],
        },
        { id: "cms-newsletter", label: "Newsletter", to: "/cms/newsletter", icon: Mail, perm: "cms:newsletter" },
        {
          id: "cms-configuration",
          label: "Configuration",
          to: "/cms",
          icon: Settings,
          children: [
            { id: "cms-categories", label: "Categories", to: "/cms/categories", icon: Tag, perm: "cms:categories" },
            { id: "cms-cookies", label: "Cookies", to: "/cms/cookies", icon: Cookie, perm: "cms:settings" },
            { id: "cms-settings", label: "Settings", to: "/cms/settings", icon: Settings, perm: "cms:settings" },
          ],
        },
      ],
    },
  ],
};

/** Every capability that admits a session to the CMS console (any one suffices). */
export const CMS_CONSOLE_PERMS = [
  "cms:announcements",
  "cms:tenders",
  "cms:events",
  "cms:publications",
  "cms:gallery",
  "cms:media",
  "cms:newsletter",
  "cms:categories",
  "cms:settings",
] as const;
